<template id="drilldown-report-page">
  <section>
    <b-toaster name="b-toaster-top-center" aria-live="polite" aria-atomic="false" />
    <div class="container">
      <div class="card shadow-sm border-0">
        <div class="card-header bg-primary text-white py-2 d-flex justify-content-center align-items-center">
          <strong><i class="fas fa-file-upload mr-2"></i>File Upload Details</strong>
        </div>

        <div class="card-body p-3">
          <!-- Grid layout: 3 fields per row -->
          <div class="form-row">
            <!-- File Identifier -->
            <div
                v-for="(item, idx) in screenletListboxDim"
                :key="idx"
                class="form-group col-md-3 mb-3"
            >
              <label class="col-form-label col-form-label-sm font-weight-bold">
                {{ SLlistboxDimBizName[idx] }}
              </label>
              <div v-if="screenletListboxType === 'anyWhichWay'">
                <select
                    v-model="fileIdentifier[item.name]"
                    @change="updateFileType"
                    class="form-control form-control-sm"
                >
                  <option
                      v-for="id in (listBoxOptions[item.name] || [])"
                      :key="id"
                      :value="id"
                  >
                    {{ id }}
                  </option>
                </select>
              </div>
            </div>

            <!-- File Type -->
            <div class="form-group col-md-2 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">File Type</label>
              <select v-model="selectedType" class="form-control form-control-sm">
                <option
                    v-for="type in fileTypes"
                    :key="type.value"
                    :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>

            <!-- From Date -->
            <div class="form-group col-md-3 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">From Date</label>
              <date-picker
                  v-model="fromDate"
                  :disabled-date="disableFutureAndToday"
                  format="YYYY-MM-DD"
                  value-type="format"
                  placeholder="Select From Date"
                  input-class="form-control form-control-sm"
              />
            </div>

            <!-- To Date -->
            <div class="form-group col-md-3 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">To Date</label>
              <date-picker
                  v-model="toDate"
                  :disabled-date="disableFutureAndToday"
                  format="YYYY-MM-DD"
                  value-type="format"
                  placeholder="Select To Date"
                  input-class="form-control form-control-sm"
              />
            </div>

            <!-- Choose File -->
            <div class="form-group col-md-7 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">Choose File</label>
              <input
                  type="file"
                  class="form-control form-control-sm"
                  ref="file"
                  :accept="selectedType"
                  @change="handleFileUpload"
              />
              <!-- Success message when file is valid -->
              <small v-if="fileValid && renamedFile" class="text-success">
                ✅ File validated successfully: {{ actualFileName }}
              </small>
              <!-- Error message when file type doesn't match -->
              <small v-else-if="fileError" class="text-danger">
                ❌ {{ fileError }}
              </small>
              <!-- Default message when no file is selected -->
              <small v-else class="text-muted">
                Allowed file type: {{ selectedType || "Select a type first" }}. Maximum size: {{ maxUploadLabel }}.
              </small>
            </div>

            <!-- Upload Reason -->
            <div class="form-group col-md-4 mb-3">
              <label class="col-form-label col-form-label-sm font-weight-bold">
                Upload Reason <span class="text-danger">*</span>
              </label>

              <!-- Select dropdown for predefined reasons -->
              <select
                  v-model="selectedUploadReason"
                  @change="onUploadReasonChange"
                  class="form-control form-control-sm mb-2"
              >
                <option value="">Select a reason</option>
                <option
                    v-for="reason in uploadReasons"
                    :key="reason"
                    :value="reason"
                >
                  {{ reason }}
                </option>
              </select>

              <!-- Manual input field - only show when "Other" is selected -->
              <input
                  v-if="showManualInput"
                  type="text"
                  v-model.trim="manualUploadReason"
                  class="form-control form-control-sm"
                  placeholder="Enter reason for upload"
                  @input="validateUploadReason"
              />

              <!-- Error message -->
              <small v-if="uploadReasonError" class="text-danger">
                {{ uploadReasonError }}
              </small>
            </div>
          </div>

          <!-- Buttons aligned to right -->
          <div class="d-flex justify-content-end mt-3">
            <button
                class="btn btn-secondary btn-sm mr-2"
                @click="cancelUpload"
            >
              <i class="fas fa-times mr-1"></i> Cancel
            </button>
            <button
                class="btn btn-primary btn-sm"
                :class="{ 'disabled-btn': isUploadDisabled }"
                :disabled="isUploadDisabled"
                @click="submitFile"
            >
              <i class="fas fa-upload mr-1"></i> Upload
            </button>
          </div>

          <!-- Filename Preview -->
          <div class="row mt-3">
            <div class="col">
              <small class="font-weight-bold text-secondary">Generated Filename:</small>
              <span class="text-success font-weight-bold text-truncate">
                {{ generatedFilename }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<script>
import axios from 'axios'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import {isDescendant} from "@/common/functions";
// import { logDebugEvent } from '@/debugLogger'

/** Fallback when `clientConfigParam.maxFileSize` is missing or invalid. */
const DEFAULT_MAX_UPLOAD_BYTES = 30 * 1024 * 1024

function parseClientConfigParam(raw) {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch (e) {
      return null
    }
  }
  return raw
}

function parseMaxFileSizeToBytes(sizeStr) {
  if (!sizeStr || typeof sizeStr !== 'string') return DEFAULT_MAX_UPLOAD_BYTES
  const match = sizeStr.trim().match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)$/i)
  if (!match) return DEFAULT_MAX_UPLOAD_BYTES
  const value = parseFloat(match[1])
  const unit = match[2].toUpperCase()
  const multipliers = { KB: 1024, MB: 1024 * 1024, GB: 1024 * 1024 * 1024 }
  return Math.floor(value * multipliers[unit])
}

function formatMaxFileSizeLabel(sizeStr) {
  if (!sizeStr) return '30 MB'
  const match = String(sizeStr).trim().match(/^(\d+(?:\.\d+)?)\s*(KB|MB|GB)$/i)
  if (!match) return String(sizeStr)
  return `${match[1]} ${match[2].toUpperCase()}`
}

/** Same character as `generatedFilename` placeholder when prerequisites are missing. */
const PLACEHOLDER_EM_DASH = '\u2014'

export default {
  name: 'file-upload',
  components: {
    DatePicker,
  },
  data: function () {
    return {
      file: '',
      selectedType: "", // default
      fileIdentifier: {},
      fileTypes: [],
      fromDate: null,
      toDate: null,
      renamedFile: "",
      screenletListboxDim:{},
      listBoxCache: {},
      SLlistboxDimBizName: {},
      listBoxOptions: {},
      uploadReason: "", // <-- Added this
      uploadReasonError: "",
      actualFileName:"",
      filePrivilege:{},
      fileValid: false,
      fileError: '',
      uploadReasons: ['Other'],
      selectedUploadReason: '',
      manualUploadReason: '',
      showManualInput: false,
      /** Raw file from the file input; we rebuild `renamedFile` when dates/identifiers change. */
      chosenFileRaw: null,
      fileSize: 0,
    }
  },
  computed: {
    maxUploadBytes() {
      const configParam = parseClientConfigParam(
          this.$store.state.fwMClient?.[0]?.clientConfigParam
      )
      return parseMaxFileSizeToBytes(configParam?.maxFileSize)
    },
    maxUploadLabel() {
      const configParam = parseClientConfigParam(
          this.$store.state.fwMClient?.[0]?.clientConfigParam
      )
      return formatMaxFileSizeLabel(configParam?.maxFileSize)
    },
    isUploadDisabled() {
      return !this.fromDate || !this.toDate || !this.fileIdentifier || !this.uploadReason;
    },
    generatedFilename() {
      if (!this.fileIdentifier || !this.fromDate || !this.toDate || !this.selectedType) {
        return PLACEHOLDER_EM_DASH;
      }

      // Collect identifier values in **listbox column order** (not Object.values order).
      const dimObjs =
          this.screenletListboxDim && typeof this.screenletListboxDim === 'object'
              ? Object.keys(this.screenletListboxDim)
                  .map((k) => this.screenletListboxDim[k])
                  .filter((d) => d && d.name)
              : []
      const orderedValues =
          dimObjs.length > 0
              ? dimObjs.map((d) => this.fileIdentifier[d.name]).filter(Boolean)
              : Object.values(this.fileIdentifier).filter(Boolean)
      const identifiers = orderedValues.map((id) => String(id).toUpperCase()).join('_')

      if (!identifiers) {
        return PLACEHOLDER_EM_DASH;
      }

      // Format dates as YYYYMMDD
      const fromDate = this.fromDate.replace(/-/g, "");
      const toDate = this.toDate.replace(/-/g, "");

      // Timestamp: YYYYMMDDHHMMSS
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp =
          now.getFullYear().toString() +
          pad(now.getMonth() + 1) +
          pad(now.getDate()) +
          pad(now.getHours()) +
          pad(now.getMinutes()) +
          pad(now.getSeconds());

      return `FNAME_${identifiers}_FROM_${fromDate}_TO_${toDate}_EXTTS_${timestamp}_SRC_FW_${this.$store.state.userName}${this.selectedType}`;
    }
  },
  async created() {
    console.log("banner flag",this.$store.state.bannerStatus)

    let restrictedMenu = this.$store.state.user.userRoleRestriction.menuAccessInclusionArray || []
    let exclusionArray = this.$store.state.user.userRoleRestriction.menuAccessExclusionArray || [];
    const menuTree = this.$store.state.menuMasterData.filter(m => m.parentMenuId === 'Root') || []

    // Handle empty/undefined/null/["[]"] cases - all menus allowed
    const isAllMenusAllowed = !restrictedMenu ||
        restrictedMenu.length === 0 ||
        (restrictedMenu.length === 1 && restrictedMenu[0] === "[]")

    if (!isAllMenusAllowed) {
      const currentMenuId = this.$route.params.menuId
      const currentSubmenuId = this.$route.params.submenuId

      // Only check menu restrictions for routes that have these parameters
      if (currentMenuId || currentSubmenuId) {
        // Check if current IDs are in exclusion array
        const isExcluded = exclusionArray.includes(currentMenuId) ||
            exclusionArray.includes(currentSubmenuId)
        // If NEITHER menuId NOR submenuId is in the allowed list, block access
        // Check whether current IDs are directly allowed or descendants of allowed ones
        const isAllowed =
            restrictedMenu.includes(currentMenuId) ||
            restrictedMenu.includes(currentSubmenuId) ||
            isDescendant(menuTree, restrictedMenu, currentMenuId) ||
            isDescendant(menuTree, restrictedMenu, currentSubmenuId);

        if (!isAllowed || isExcluded) {
          window.alert("Invalid action, request please contact your Administrator")
          // You might want to redirect to user-restrict page here as well
          await this.$router.push('/user-restrict')
          return; // Stop execution
        }
      }
    }
    this.listenToRouter()
    this.filter = {...this.screenletListboxDefault}

  },
  watch: {
    $props: {
      async handler() {
        await this.loadListbox();
      },
      deep: true,
      immediate: true,
    },
    iframeSrc(newSrc) {
      this.iframeKey = `${newSrc}?t=${Date.now()}`;
    },
    fromDate() {
      this.syncRenamedFileFromChosen();
    },
    toDate() {
      this.syncRenamedFileFromChosen();
    },
    selectedType() {
      this.syncRenamedFileFromChosen();
    },
    fileIdentifier: {
      handler() {
        this.syncRenamedFileFromChosen();
      },
      deep: true,
    },
  },
  methods: {
    /**
     * User-visible toast (Bootstrap-Vue).
     */
    toastFileUpload(variant, title, message, autoHideDelay) {
      this.$bvToast.toast(message, {
        title: title || (variant === 'success' ? 'Success' : 'Notice'),
        variant: variant || 'info',
        solid: true,
        autoHideDelay: autoHideDelay != null ? autoHideDelay : (variant === 'danger' ? 10000 : 6000),
        toaster: 'b-toaster-top-center'
      })
    },
    /**
     * Structured diagnostics: browser console + optional backend debug log.
     */
    // logFileUploadEvent(context, detail, error) {
    //   const route = {
    //     menuId: this.$route.params.menuId,
    //     submenuId: this.$route.params.submenuId
    //   }
    //   const payload =
    //       detail && typeof detail === 'object' && !Array.isArray(detail)
    //           ? { context, ...route, ...detail }
    //           : { context, ...route, message: detail }
    //   if (error) {
    //     console.warn('[file-upload]', context, detail, error)
    //     if (error.response) {
    //       console.warn('[file-upload] response status', error.response.status, error.response.data)
    //     }
    //     logDebugEvent({
    //       type: 'file-upload-error',
    //       ...payload,
    //       errorName: error.name,
    //       errorMessage: error.message,
    //       responseStatus: error.response && error.response.status,
    //       responseData: error.response && error.response.data
    //     })
    //   } else {
    //     console.log('[file-upload]', context, detail)
    //     logDebugEvent({
    //       type: 'file-upload',
    //       ...payload
    //     })
    //   }
    // },
    fileExceedsMaxSize(file) {
      this.fileSize = file.size;
      return file && file.size != null && file.size > this.maxUploadBytes
    },
    formatSizeForMessage(bytes) {
      if (bytes == null || isNaN(bytes)) return ''
      const mb = bytes / (1024 * 1024)
      if (mb >= 0.1) return `${mb.toFixed(1)} MB`
      const kb = bytes / 1024
      return `${kb.toFixed(0)} KB`
    },
    onUploadReasonChange() {
      this.uploadReasonError = '';

      if (this.selectedUploadReason === 'Other') {
        this.showManualInput = true;
        this.uploadReason = ''; // Clear until user types manually
      } else {
        this.showManualInput = false;
        this.manualUploadReason = ''; // Clear manual input
        this.uploadReason = this.selectedUploadReason; // Set the selected reason
        this.validateUploadReason();
      }

      console.log("upload Reaons",this.uploadReason);
    },
    disableFutureAndToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date >= today; // disables today and all future dates
    },
    cancelUpload() {
      this.fromDate = null;
      this.toDate = null;
      this.uploadReason = '';
      this.selectedType = '';
      this.fileIdentifier = {};
      if (this.$refs.file) this.$refs.file.value = '';
      this.selectedUploadReason = "";
      this.actualFileName = "";
      this.renamedFile = "";
      this.chosenFileRaw = null;
      this.fileValid = false;
      this.fileError = "";
    },
    /**
     * Match dataset row from selected listbox dimensions (fileIdentifier keys are column names).
     * `fileIdentifier.src_identifier_prefix` is never set by the template — do not use it.
     */
    resolveMatchedDatasetRow() {
      if (!Array.isArray(this.dataset) || this.dataset.length === 0) {
        return null
      }
      const dimObjs =
          this.screenletListboxDim && typeof this.screenletListboxDim === 'object'
              ? Object.keys(this.screenletListboxDim)
                  .map((k) => this.screenletListboxDim[k])
                  .filter((d) => d && d.name)
              : []
      if (dimObjs.length === 0) {
        return this.dataset.length === 1 ? this.dataset[0] : null
      }
      return (
          this.dataset.find((row) =>
              dimObjs.every((dim) => {
                const key = dim.name
                const selected = this.fileIdentifier[key]
                if (selected === null || selected === undefined || selected === '') {
                  return false
                }
                const cell = row[key]
                return cell === selected || String(cell) === String(selected)
              })
          ) || null
      )
    },
    getSrcIdentifierPrefix() {
      const row = this.resolveMatchedDatasetRow()
      if (row && row.src_identifier_prefix != null) {
        return String(row.src_identifier_prefix)
      }
      return ''
    },
    syncRenamedFileFromChosen() {
      if (!this.chosenFileRaw) {
        return
      }
      const g = this.generatedFilename
      const invalid =
          !g ||
          g === PLACEHOLDER_EM_DASH ||
          g === '—' ||
          !String(g).startsWith('FNAME_')
      if (invalid) {
        this.fileValid = false
        this.renamedFile = null
        this.fileError =
            'Select From Date, To Date, and all file identifier fields so the server filename can be generated.'
        return
      }
      const fileExtension = '.' + this.chosenFileRaw.name.split('.').pop().toLowerCase()
      const selectedExtension = (this.selectedType || '').toLowerCase()
      if (!selectedExtension || fileExtension !== selectedExtension) {
        this.fileValid = false
        this.renamedFile = null
        this.fileError =
            this.selectedType != null && this.selectedType !== ''
                ? `Allowed file type is ${String(this.selectedType).toUpperCase()}.`
                : 'Select a file type that matches your file.'
        return
      }
      if (this.fileExceedsMaxSize(this.chosenFileRaw)) {
        this.fileValid = false
        this.renamedFile = null
        return
      }
      this.fileError = ''
      this.fileValid = true
      this.renamedFile = new File([this.chosenFileRaw], g, { type: this.chosenFileRaw.type })
      this.actualFileName = this.chosenFileRaw.name
    },
    updateFileType() {
      const matched = this.resolveMatchedDatasetRow()
      console.log(matched);
      if (matched && matched.src_file_type_or_database) {
        this.selectedType = '.' + String(matched.src_file_type_or_database)
      }
      console.log("selected Type",this.selectedType)

      // Handle the reactive object properly
      let filePrivileges = matched ? matched.file_conditions_json : {};
      console.log("filePrivileges", filePrivileges);

      // If filePrivileges has a value property (reactive object), use that
      if (filePrivileges && filePrivileges.value) {
        filePrivileges = filePrivileges.value;
      }

      // Parse the JSON string if it's a string
      if (typeof filePrivileges === 'string') {
        try {
          filePrivileges = JSON.parse(filePrivileges);
        } catch (e) {
          console.error('Error parsing file_privileges JSON:', e);
          filePrivileges = {};
        }
      }

      console.log("parsed filePrivileges", filePrivileges);

      // Get the matched privilege object
      this.filePrivilege = this.findMatchedPrivilege(filePrivileges);
      this.uploadReasons = filePrivileges?.upload_reason
      console.log("this", this.filePrivilege,this.uploadReasons);
    },
    findMatchedPrivilege(filePrivileges) {
      if (!filePrivileges || !filePrivileges.file_privileges) {
        return {
          role: "",
          user: "",
          trust_level: "defer_manual"
        }; // Default privilege object if no privileges defined
      }

      const currentUser = this.$store.state.userName; // Replace with actual current user from login
      const currentRole = this.$store.state.roleName; // Replace with actual current role from login

      const privileges = filePrivileges.file_privileges;

      // First check for exact user and role match (trust_level 1)
      const exactMatch = privileges.find(p =>
          p.role === currentRole && p.user === currentUser
      );
      if (exactMatch) return exactMatch;

      // Then check for role match only (trust_level 2)
      const roleMatch = privileges.find(p =>
          p.role === currentRole && p.user === ""
      );
      if (roleMatch) return roleMatch;

      // Finally check for empty role and user (trust_level 3)
      const defaultMatch = privileges.find(p =>
          p.role === "" && p.user === ""
      );
      if (defaultMatch) return defaultMatch;

      // Return default privilege object if no matches found
      return {
        role: "",
        user: "",
        trust_level: "defer_manual"
      };
    },
    listenToRouter: function () {
      console.log("listenToRouter is called")
      let $this = this
      this.$router.afterEach(function (from, to) {
        console.log(`router moved to ${from.path} from ${to.path}`)
        $this.rows = []
        $this.screenletListboxDim = {}
        $this.screenletListboxCount = null
        $this.optionValues = []
        $this.isOpenCoursel = false
        $this.messages = []
        //$this.x = {"d3l1_name":"All","d6l1_name":"All"},//need to revisit to make it dynamic
        $this.x = {}//revisited and made dynamic
        $this.y = {}
        $this.filter = {}
        $this.time3 = ''
        setTimeout(() => {
          if (from.matched.some(record => record.meta.requiresScreenConfig)) {
            console.log("inside if condition",from.matched)
            $this.loadListbox()
          }
          // $this.loadSummary($this.x)
        }, 300)

      })
    },
    fetchListboxData: async function (sqlId) {
      let res = await axios.get('/api/fetchListboxData/' + sqlId, {
        headers: {
          'info-params': JSON.stringify({
            menuId: this.$route.params.menuId,
            submenuId: this.$route.params.submenuId
          }),
          'Content-Type': 'application/json'
        }
      })
      const removeEmptyOrNull = (obj) => {
        Object.keys(obj).forEach(k =>
            (obj[k] && typeof obj[k] === 'object') && removeEmptyOrNull(obj[k]) ||
            (!obj[k] && obj[k] !== undefined) && delete obj[k]
        );
        return obj;
      };
      let restrictedArray = this.$store.state.user.userRoleRestriction.menuAccessInclusionArray || []

// Handle empty/undefined/null/["[]"] cases - all menus allowed
      const isAllMenusAllowed = !restrictedArray ||
          restrictedArray.length === 0 ||
          (restrictedArray.length === 1 && restrictedArray[0] === "[]")

      let data = this.$route.params.submenuId === "AB" ? res.data.filter(item => {
        // If all menus are allowed, return all items
        if (isAllMenusAllowed) {
          return true;
        }
        // Otherwise, only return items that are NOT in the restricted array
        return restrictedArray.includes(item.menu_id) || restrictedArray.includes(item.parent_menu_id);
      }) : res.data
      let listboxData = data
      this.dataset = removeEmptyOrNull(listboxData) //removes the null and empty values in the resultset
      console.log("filter from fetch", listboxData);
      const extractedTypes = [
        ...new Set(
            listboxData
                .map(item => item?.src_file_type_or_database)
                .filter(type => type && type.trim() !== "")
        ),
      ];

      if (extractedTypes.length > 0) {
        this.fileTypes = extractedTypes.map(type => ({
          label: type.toUpperCase(),
          value: '.'+type,
        }));
      } else {
        this.fileTypes = [
          { label: "Excel (.xlsx)", value: ".xlsx" },
          { label: "CSV (.csv)", value: ".csv" },
          { label: "PDF (.pdf)", value: ".pdf" },
          { label: "ODS (.ods)", value: ".ods" },
        ];
      }


      this.listBoxOptions = await this.makeListBoxOptions();
      console.log(this.listBoxOptions)
    },
    async makeListBoxOptions() {
      let listboxData = this.dataset
      console.clear()

      const makeOptions = (records, filterKeys, cacheKey) => {
        //make distinct values of each col
        //total cols = 4

        //cacheKey = simply, length of row + filterKeys

        const getDistinctColValues = () => {

          //for each key, we make a set to hold distinct values
          let localFilters = Object.fromEntries(
              filterKeys.map(k => [k, new Set()])
          )

          //parse through each record
          for (let row of records) {
            //for every filter-key, we add to set, this will de-dup the data
            for (let fk of filterKeys) {
              localFilters[fk].add(row[fk])
            }
          }

          //convert to a map of {k1: [distnct values for k1], k2: [distinct values for k2] .... etc}
          return Object.fromEntries(Object.entries(localFilters).map(x => {
            return [x[0], [...x[1]].sort()]
          }))
        }

        let fromCache = false; //this.listBoxCache[cacheKey]
        //let fromCache = false
        if (cacheKey && fromCache) {
          return fromCache;
        } else {
          // let ts = new Date().getTime()
          let value = getDistinctColValues();
          this.listBoxCache[cacheKey] = value;
          return value;

        }

      }

      const applyNextFilter = (currentFilter, restOfFilters, appliedFilters) => {

        let colSelected = currentFilter[0],
            colValues = currentFilter[1];

        //say 3 filters
        //salesman, brand, store
        //when nothing is selected, then distinct values of each can be used (default)
        //say one is selected, then we use that and select distinct values for rest
        //say two is selected, then use both to select distinct values for rest


        //filter records based on applied filters
        let listboxdataCopy = [...listboxData]

        for (let restFilter of restOfFilters) {
          if (restFilter[1].length > 0) {
            listboxdataCopy = listboxdataCopy.filter(x => restFilter[1].indexOf(x[restFilter[0]]) > -1);
          }
        }

        let optionsAfterRest = makeOptions(
            listboxdataCopy,
            [colSelected],
            [...Object.entries(restOfFilters), currentFilter].map(x => `${x[0]}:${x[1].join('-')}`).join()
        )

        //select records, by applying the current filter
        let recordsAfterFilter = listboxdataCopy.filter(x => colValues.indexOf(x[colSelected]) > -1);

        //from filters, remove the current filter, and previously applied filters
        let filterRemainingKeys = Object
            .keys(listboxData[0])
            .filter(x => x !== colSelected)
            .filter(x => !appliedFilters.map(y => y[0]).includes(x))

        //now we would have gotten, distinct col values for remaining filter, based on remaining data
        let options = makeOptions(
            recordsAfterFilter,
            filterRemainingKeys,
            Object.entries(this.filter).map(x => `${x[0]}:${x[1].join('-')}`).join()
        )
        return {...options, ...optionsAfterRest}
      }

      if (this.dataset.length > 0) {

        //if filter values are selected, then apply
        let initialFilterValues = makeOptions(listboxData, Object.keys(listboxData[0]), "ALL")
        let nextFilter = null;
        //iterate through each filter
        let filterEntries = Object.entries(this.fileIdentifier);
        let appliedFilters = []
        for (let currFilter of filterEntries) {

          let restOfFilters = filterEntries.filter(x => x[0] !== currFilter[0])

          //if the filter is selected
          if (currFilter[1].length > 0) {

            let prevFilter = {...(nextFilter || initialFilterValues)}
            let appliedFilter = applyNextFilter(
                currFilter,
                restOfFilters,
                appliedFilters
            )
            appliedFilters.push(currFilter)

            nextFilter = {...prevFilter, ...appliedFilter}
          }

          //else, skip this filter
        }
        return nextFilter || initialFilterValues

      } else {
        return {}

      }
    },
    loadListbox: async function () {
      let submenuParam = this.$route.params.submenuId
      console.log("submenuParma", submenuParam);
      await this.$store.dispatch("loadState")
      this.screenName = this.$store.state.menuMasterFlat.filter(m => m.menuId === submenuParam).map(m => m.menuName)[0]
      let menuMasterData = this.$store.state.menuMasterFlat.filter(m => m.menuId === submenuParam)
      console.log("menuMasterData", menuMasterData)
      let menuData = this.$store.state.fwConfigMenuScreenRender
      console.log("menuData", menuData);
      let columnMetadata = this.$store.state.colummnMetaData
      let listboxData = this.$store.state.listbox
      let currentMenuData = [];
      if (this.screenletId) {
        currentMenuData = menuData.filter(r => r.menuId === submenuParam && r.screenletInstanceId === this.screenletId)
      } else {
        currentMenuData = menuData.filter(r => r.menuId === submenuParam)
      }

      let screenletListboxSyspkArray = currentMenuData[0]?.screenletListboxSyspkArray ? currentMenuData[0]?.screenletListboxSyspkArray : []
      let currentMenuListboxData = listboxData.filter(r => screenletListboxSyspkArray.includes(r.syspk))

      function sortFuncLB(a, b) {
        return screenletListboxSyspkArray.indexOf(a.syspk) - screenletListboxSyspkArray.indexOf(b.syspk);
      }

      currentMenuListboxData.sort(sortFuncLB); //sorting the currentMenuListboxData

      this.currentMenuListboxData = currentMenuListboxData
      this.screenletListboxCount = currentMenuListboxData.length
      let currentMenuListboxSQLId = currentMenuData[0].screenletListbox1_sqlId
      this.screenletListboxType = currentMenuData[0].screenletListboxType
      if (currentMenuListboxSQLId) {
        await this.fetchListboxData(currentMenuListboxSQLId);
      }

      currentMenuListboxData.forEach((cmld, index) => {
        console.log("cmld", cmld);
        let bizNamefilterdim = columnMetadata.filter(c => c.columnName === cmld.listboxDimName);

        this.$set(this.screenletListboxDim, index, {
          name: cmld.listboxDimName,  // The key name you were using before
          isInputField: cmld.inputField  // The new input_field boolean
        });
        // Log the newly added item
        console.log('Added item:', {
          index: index,
          name: cmld.listboxDimName,
          isInputField: cmld.inputField
        });

// Log the entire array
        console.log('Current screenletListboxDim:', JSON.parse(JSON.stringify(this.screenletListboxDim)));

        this.$set(this.SLlistboxDimBizName, index, bizNamefilterdim[0]?.bizName || null);
      });
    },
    getFileExtension(filename) {
      if (filename == null || filename === '') {
        return ''
      }
      const base = String(filename).replace(/^.*[/\\]/, '')
      const dot = base.lastIndexOf('.')
      if (dot <= 0 || dot >= base.length - 1) {
        return ''
      }
      return base.substring(dot + 1)
    },
    validateUploadReason() {
      this.uploadReasonError = '';

      // If "Other" is selected, use the manual input
      if (this.selectedUploadReason === 'Other') {
        this.uploadReason = this.manualUploadReason;
      }

      // Validation logic
      if (!this.uploadReason) {
        this.uploadReasonError = 'Upload reason is required';
        return false;
      }

      if (this.uploadReason.length < 2) {
        this.uploadReasonError = 'Upload reason must be at least 2 characters long';
        return false;
      }

      return true;
    },
    submitFile() {
      this.validateUploadReason();
      if (!this.renamedFile) {
        // this.logFileUploadEvent('submit-validation', 'No file selected')
        this.toastFileUpload('warning', 'File required', 'Please choose a file to upload.')
        return
      }
      if (this.fileExceedsMaxSize(this.renamedFile)) {
        const msg =
            `This file is ${this.formatSizeForMessage(this.renamedFile.size)}. Maximum allowed size is ${this.maxUploadLabel}.`
        // this.logFileUploadEvent('submit-validation', { reason: 'file-too-large', size: this.renamedFile.size })
        this.toastFileUpload('danger', 'File too large', msg)
        return
      }
      if (this.uploadReasonError) {
        return
      }
      if (
          !this.renamedFile.name ||
          !String(this.renamedFile.name).startsWith('FNAME_')
      ) {
        this.syncRenamedFileFromChosen()
        if (
            !this.renamedFile ||
            !this.renamedFile.name ||
            !String(this.renamedFile.name).startsWith('FNAME_')
        ) {
          // this.logFileUploadEvent('submit-validation', 'generated-filename-not-ready')
          this.toastFileUpload(
              'warning',
              'Upload name not ready',
              'Complete From Date, To Date, and all identifier fields, then choose the file again (or change a field to refresh the name).'
          )
          return
        }
      }
      this.checkFileExist(this.renamedFile.name)
    },
    resetFile() {
      if (this.$refs.file) {
        this.$refs.file.value = ''
      }
      this.chosenFileRaw = null
      this.renamedFile = null
      this.fileValid = false
    },
    //Handles a change on the file upload
    handleFileUpload(event) {
      const file = event.target.files[0];
      console.log("file", file);

      // Reset states
      this.fileValid = false;
      this.fileError = '';
      this.renamedFile = null;
      this.actualFileName = '';
      this.chosenFileRaw = null;

      if (!file) return;

      // Check if selectedType is set
      if (!this.selectedType) {
        this.fileError = 'Please select a file type first.';
        event.target.value = '';
        return;
      }

      // Require a real generated name (pick file only after dates + identifiers)
      const gen = this.generatedFilename;
      if (
          !gen ||
          gen === PLACEHOLDER_EM_DASH ||
          gen === '—' ||
          !String(gen).startsWith('FNAME_')
      ) {
        this.fileError =
            'Please select From Date, To Date, and all file identifier fields before choosing a file.';
        event.target.value = '';
        return;
      }

      // Get file extension from actual file
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      const selectedExtension = this.selectedType.toLowerCase();

      console.log("File extension:", fileExtension);
      console.log("Selected type:", selectedExtension);

      // Validate file type
      if (fileExtension !== selectedExtension) {
        this.fileError = `Allowed file type is ${this.selectedType.toUpperCase()}. You selected a ${fileExtension.toUpperCase()} file.`;
        event.target.value = '';
        return;
      }

      if (this.fileExceedsMaxSize(file)) {
        const msg =
            `This file is ${this.formatSizeForMessage(file.size)}. Maximum allowed size is ${this.maxUploadLabel}.`
        this.fileError = msg
        // this.logFileUploadEvent('file-selected', { reason: 'file-too-large', size: file.size, name: file.name })
        this.toastFileUpload('danger', 'File too large', msg)
        event.target.value = ''
        return
      }

      this.chosenFileRaw = file
      this.syncRenamedFileFromChosen()
      console.log('✅ Renamed file:', this.renamedFile && this.renamedFile.name, this.generatedFilename);
    },

    checkFileExist(fileName) {
      var self = this;
      axios.get(`/api/exists?fileName=${fileName}`)
          .then(function (resp) {
            if (resp.data.exists) {
              if (confirm("File Already Exists, Do you want to overwrite it?")) {
                self.file_load();
              } else {
                self.resetFile();
              }
            } else {
              self.file_load();
            }
          }, function (error) {
            console.log("error", error);
            // self.logFileUploadEvent('exists-check-failed', { fileName: fileName }, error)
            self.toastFileUpload(
                'danger',
                'Could not verify file',
                'Error checking whether the file already exists. Check the connection and try again.'
            )
          })
    },
    file_load: function () {
      const self = this; // Store reference to Vue instance

      if (!self.renamedFile || self.fileExceedsMaxSize(self.renamedFile)) {
        const msg =
            self.renamedFile && self.fileExceedsMaxSize(self.renamedFile)
                ? `This file is ${self.formatSizeForMessage(self.renamedFile.size)}. Maximum allowed size is ${self.maxUploadLabel}.`
                : 'No file selected for upload.'
        // self.logFileUploadEvent('file_load-blocked', { reason: !self.renamedFile ? 'no-file' : 'file-too-large' })
        self.toastFileUpload('danger', 'Upload not started', msg)
        return
      }

      let formData = new FormData();
      formData.append('file', this.renamedFile);
      formData.append('actualFileName',this.actualFileName);
      formData.append('reason',this.uploadReason);
      formData.append('filePrivilege',JSON.stringify(this.filePrivilege));
      const idPrefix = this.getSrcIdentifierPrefix()
      if (!idPrefix) {
        // self.logFileUploadEvent('file_load-blocked', {
        //   reason: 'no-src-identifier-prefix',
        //   fileIdentifier: { ...self.fileIdentifier },
        // })
        self.toastFileUpload(
            'warning',
            'File identifier missing',
            'Your listbox selections do not match any configured upload row. Adjust the identifier fields so they match one row in the configuration, then try again.'
        )
        return
      }
      formData.append('fileIdentifier', idPrefix)
      let file_name = this.renamedFile.name;
      let file_extn = this.getFileExtension(file_name);
      if (!file_extn && this.selectedType) {
        file_extn = String(this.selectedType).replace(/^\./, '')
      }
      console.log('file_extn', file_extn, 'file_name', file_name, 'src_identifier_prefix', idPrefix)
      const allowedTypes = ["xlsx", "csv", "pdf", "ods"];

      if (!allowedTypes.includes(file_extn.toLowerCase())) {
        const notAllowedMsg =
            'File identifier ' +
            (idPrefix || '(unknown)') +
            ' File Extension .' +
            file_extn +
            ' is not allowed. Please choose one of: ' +
            allowedTypes.join(', ')
        // self.logFileUploadEvent('extension-not-allowed', { file_extn: file_extn, file_name: file_name })
        self.toastFileUpload('danger', 'File type not allowed', notAllowedMsg)
        if (self.$refs.file) self.$refs.file.value = ''
        return
      }

      // self.logFileUploadEvent('upload-start', { file_name: file_name, size: self.renamedFile.size })

      axios
          .post('/api/upload', formData)
          .then(function () {
            // self.logFileUploadEvent('upload-success', { file_name: file_name })
            self.toastFileUpload('success', 'Upload complete', file_name + ' uploaded successfully.')

            self.selectedType = '.xlsx'
            self.fileIdentifier = {}
            self.fromDate = ''
            self.toDate = ''
            self.renamedFile = ''
            self.chosenFileRaw = null
            self.file = ''

            if (self.$refs.file) self.$refs.file.value = ''
            setTimeout(() => {
              window.location.reload()
            }, 5000);
          })
          .catch(function (error) {
            const status = error.response && error.response.status
            const data = error.response && error.response.data
            let userMessage = file_name + ' could not be uploaded.'
            if (status === 400 && data && data.error) {
              userMessage = typeof data.error === 'string' ? data.error : userMessage
            } else if (status === 413) {
              userMessage = 'The file is too large for the server to accept. Maximum size is ' + self.maxUploadLabel + '.'
            } else if (status) {
              userMessage += ' (HTTP ' + status + ')'
            } else if (error.message) {
              userMessage += ' ' + error.message
            }
            const requestBody = {
              errorCode: 400,
              errorMsg: error.message,
              menuId: self.$route.params.menuId,
              submenuId: self.$route.params.submenuId,
              errorIdentifier: file_name +","+ file_extn +","+ self.fileSize +","+ idPrefix +","+ JSON.stringify(self.filePrivilege)
            } 
            console.log("requestBody", requestBody);
            axios.post('/api/userAlert', requestBody)

            // self.logFileUploadEvent(
            //     'upload-failed',
            //     {
            //       file_name: file_name,
            //       http_status: status,
            //       response_data: data
            //     },
            //     error
            // )
            self.toastFileUpload('danger', 'Upload failed', userMessage)
            if (self.$refs.file) self.$refs.file.value = ''
          })
    }
  }
}
</script>
<style>
/*.v-input--selection-controls .v-input__control {
  width: 100%;
}*/
.container {
  max-width: 1200px;
}

.card {
  border-radius: 10px;
}

.form-group label {
  color: #333;
}

/* Upload button disabled style */
.disabled-btn {
  background-color: #d6d6d6 !important;
  border-color: #ccc !important;
  color: #777 !important;
  cursor: not-allowed !important;
  pointer-events: none;
}

/*button.btn {
  min-width: 100px;
}*/

.card-body {
  background-color: #fdfdfd;
}

/* Fix vertical alignment for vue2-datepicker fields */
.form-group .mx-datepicker {
  width: 100%;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  color: #333;
}

/* Align datepicker with other input fields */
.mx-input {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
  border: 1px solid #ced4da;
}

.mx-input-wrapper {
  display: block !important;
  width: 100% !important;
}

</style>